import { Card, Button, CryptoIcon, getNetworkName } from '@valiant/ui-shared'
import { RefreshCw } from 'lucide-react'
import { useGetCompanyBalanceQuery } from '../../generated/graphql'

interface HoldingsProps {
  companyId: string
  onRefresh: () => void
  isRefreshing: boolean
}

export function Holdings({ companyId, onRefresh, isRefreshing }: HoldingsProps) {
  // Fetch company balance
  const [{ data: balanceData, fetching: balanceLoading }] = useGetCompanyBalanceQuery({
    variables: { companyId },
    pause: !companyId,
  })

  const finalHoldings = balanceData?.getCompanyBalance.holdings.filter(
    (h) => parseFloat((h.balance || '0').replace(/,/g, '')) > 0,
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">Your Holdings</h2>
        <Button
          variant="main_action"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="h-[39px]"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {balanceLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
                <div className="space-y-2">
                  <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-20"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : finalHoldings?.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {finalHoldings.map((holding) => (
            <Card
              key={holding.mint.id}
              className="bg-transparent px-4 md:px-6 xl:px-9 py-3 md:py-4 lg:py-6"
            >
              <div className="space-y-4 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <CryptoIcon
                    symbol={holding.mint.symbol}
                    imageOverride={holding.mint.image || undefined}
                    className="md:w-16 md:h-16 w-12 h-12"
                  />
                </div>

                <div>
                  <div className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 pb-3">
                    {holding.balance}
                  </div>
                  <div className="text-sm font-bold text-gray-600">{holding.mint.symbol}</div>
                  <div className="text-sm text-gray-400">{getNetworkName(holding.mint.chain)}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">No holdings found for this company.</p>
        </div>
      )}
    </div>
  )
}
